<template>
  <cst-page-template class="authorization-module-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page ds-page--fill">
      <div class="ds-page__header">
        <h1>{{ i18n.t('/authorisationmodule/title') }}</h1>
      </div>
      <p class="ds-page__intro">Beheer rollen en merkrechten per gebruiker.</p>

      <div class="authorization-module-page__toolbar">
        <div class="authorization-module-page__search">
          <ds-icon name="search" />
          <input
            v-model="searchQuery"
            data-qa="search-authorization-module-input"
            type="search"
            class="ds-field"
            placeholder="Zoeken..."
            title="zoek"
          />
        </div>

        <div
          class="ds-segmented"
          role="radiogroup"
          aria-label="Weergave"
          data-qa="view-mode-radio-button"
        >
          <button
            v-for="mode in viewModes"
            :key="mode.value"
            type="button"
            class="ds-segmented__option"
            :class="{ 'ds-segmented__option--active': selectedViewMode === mode.value }"
            role="radio"
            :aria-checked="selectedViewMode === mode.value"
            @click="selectedViewMode = mode.value"
          >
            {{ mode.name }}
          </button>
        </div>

        <div v-if="canModifyAdminRights" class="authorization-module-page__dealer">
          <svg
            class="authorization-module-page__dealer-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 21V5a1 1 0 011-1h5a1 1 0 011 1v16M14 21V9h5a1 1 0 011 1v11M8 8h1M8 12h1M8 16h1"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="authorization-module-page__dealer-label">Dealer</span>
          <dealer-combobox
            id="authorization-dealer"
            v-model="searchDealerId"
            @select="fetchAllUserRoles"
          />
        </div>
      </div>

      <div v-if="isLoading" class="ds-empty">
        <ds-spinner label="Gebruikers laden" />
        <p class="ds-empty__message">Gebruikers laden…</p>
      </div>

      <div v-else-if="!tableData.length" class="ds-empty">
        <h2 class="ds-empty__title">Geen gebruikers gevonden</h2>
        <p class="ds-empty__message">
          {{
            canModifyAdminRights
              ? 'Pas het dealerfilter aan.'
              : 'Er zijn geen gebruikers voor deze dealer.'
          }}
        </p>
      </div>

      <div
        v-else
        class="ds-table"
        :class="{ 'authorization-module-page__table--rotated': !isGroupByRole }"
      >
        <table-filter-chips
          v-if="activeFilterChips.length"
          :chips="activeFilterChips"
          @remove="removeFilter"
          @clear-all="clearFilters"
        />

        <div class="ds-table__scroll">
          <table :style="{ minWidth: tableMinWidth }">
            <sortable-table-header
              :columns="tableColumns"
              :filters="filters"
              :sort-column="sortColumn"
              :sort-direction="sortDirection"
              :has-actions="true"
              @toggle-sort="toggleSort"
              @set-filter="setFilter"
              @clear-filters="clearMultipleFilters"
            >
              <template v-for="col in matrixColumns" :key="col.key" #[matrixSlotName(col)]>
                <div v-if="isGroupByRole" class="authorization-module-page__matrix-head">
                  <div class="authorization-module-page__matrix-role-label">{{ col.label }}</div>
                  <div class="authorization-module-page__matrix-brands">
                    <span
                      v-for="brand in brands"
                      :key="brand.code"
                      :class="getBrandBadgeClass(brand.code)"
                    >
                      {{ brand.textShort }}
                    </span>
                  </div>
                </div>
                <div
                  v-else
                  class="authorization-module-page__matrix-head authorization-module-page__matrix-head--brand"
                >
                  <div class="authorization-module-page__matrix-roles">
                    <div v-for="role in rolesHeaders" :key="role.value">
                      <div>{{ role.text }}</div>
                    </div>
                  </div>
                  <div
                    class="authorization-module-page__matrix-brand"
                    :class="`authorization-module-page__matrix-brand--${getBrandUrlByCode(col.brandCode)}`"
                  >
                    {{ col.label }}
                  </div>
                </div>
              </template>
            </sortable-table-header>
            <tbody>
              <tr v-for="row in pagedRows" :key="`${row.DealerId}:${row.UserPonId}`">
                <td class="authorization-module-page__user-cell">
                  <span class="authorization-module-page__user">{{ row.UserPonId }}</span>
                </td>
                <td>
                  <span v-if="row.LastLoginDate">{{ row.LastLoginDate }}</span>
                  <span v-else class="authorization-module-page__nodate">Nog niet ingelogd</span>
                </td>
                <td>{{ row.ChangedDate || '—' }}</td>
                <td>{{ row.ChangedBy || '—' }}</td>

                <template v-if="isGroupByRole">
                  <td
                    v-for="role in rolesHeaders"
                    :key="role.value"
                    class="authorization-module-page__matrix-cell"
                  >
                    <span class="authorization-module-page__chiprow">
                      <button
                        v-for="brand in brands"
                        :key="brand.code"
                        type="button"
                        class="authorization-module-page__chip"
                        :style="{ background: brandColor(brand.code) }"
                        :disabled="isChipDisabled(role.value)"
                        :title="chipTitle(row, role, brand)"
                        @click="requestToggle(row, brand.code, role.value)"
                      >
                        <svg
                          v-if="isAllowed(row, role.value, brand.code)"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M5 12l5 5L19 7"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        <svg
                          v-else
                          class="authorization-module-page__chip-off"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M6 12h12"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </td>
                </template>
                <template v-else>
                  <td
                    v-for="brand in brands"
                    :key="brand.code"
                    class="authorization-module-page__matrix-cell authorization-module-page__matrix-cell--stretch"
                  >
                    <span
                      class="authorization-module-page__chiprow authorization-module-page__chiprow--stretch"
                    >
                      <button
                        v-for="role in rolesHeaders"
                        :key="role.value"
                        type="button"
                        class="authorization-module-page__chip authorization-module-page__chip--stretch"
                        :style="{ background: brandColor(brand.code) }"
                        :disabled="isChipDisabled(role.value)"
                        :title="chipTitle(row, role, brand)"
                        @click="requestToggle(row, brand.code, role.value)"
                      >
                        <svg
                          v-if="isAllowed(row, role.value, brand.code)"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M5 12l5 5L19 7"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        <svg
                          v-else
                          class="authorization-module-page__chip-off"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M6 12h12"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </td>
                </template>

                <td>
                  <div class="ds-table__actions">
                    <button
                      class="ds-icon-button"
                      title="Alle rechten verwijderen"
                      data-qa="quote-row-button-delete"
                      @click.stop="showDeleteConfirmModal(row)"
                    >
                      <ds-icon name="trash" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!pagedRows.length">
                <td :colspan="tableColumns.length + 1" class="ds-table__empty">
                  Geen gebruikers gevonden voor de huidige filters.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ds-pagination
          v-model:page="currentPage"
          v-model:page-size="pageSize"
          :total="sortedRows.length"
        />
      </div>
    </div>

    <ds-confirm-dialog
      id="confirm-update-right-prompt"
      :show="!!pendingAction"
      :title="i18n.t('/authorisationmodule/prompt/title')"
      :body="confirmActionText"
      :confirm-label="confirmButtonLabel"
      :danger="pendingAction && pendingAction.actionType !== 'ADD'"
      :confirm-disabled="actionInProgress"
      @confirm="confirmAction"
      @cancel="closeActionPopup"
    />

    <div v-if="toast" class="ds-toast-host">
      <div class="ds-toast" :class="`ds-toast--${toast.type}`">
        <span>{{ toast.message }}</span>
        <button class="ds-toast__close" aria-label="Sluiten" @click="dismissToast">&times;</button>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import authorizationService from '@/services/authorizationService'
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import SortableTableHeader from '@/pages/warningbanner/SortableTableHeader.vue'
import TableFilterChips from '@/pages/warningbanner/TableFilterChips.vue'
import DealerCombobox from '@/components/generic/presentational/molecules/dealerCombobox/DealerCombobox.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import DsPagination from '@/components/ds/molecules/dsPagination/DsPagination.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import moment from 'moment/moment'
import { mapGetters, mapState } from 'vuex'
import { getBrandBadgeClass, getBrandUrlByCode } from '@/helpers/BrandHelper'

// Matches .ds-table__header-cell--actions.
const ACTIONS_COLUMN_WIDTH = 90

// Width of one role slot inside a brand column in group-by-merk mode.
const ROLE_SLOT_WIDTH = 55

export default {
  name: 'AuthorizationModulePage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    SortableTableHeader,
    TableFilterChips,
    DealerCombobox,
    DsIcon,
    DsSpinner,
    DsPagination,
    DsConfirmDialog,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      isLoading: false,
      searchQuery: '',
      viewModes: [
        { value: 'GROUP_BY_ROLE', name: 'Groeperen op rol' },
        { value: 'GROUP_BY_BRAND', name: 'Groeperen op merk' },
      ],
      selectedViewMode: 'GROUP_BY_ROLE',
      searchDealerId: null,
      filters: {
        UserPonId: '',
        ChangedBy: '',
      },
      sortColumn: null,
      sortDirection: 'asc',
      currentPage: 1,
      pageSize: 25,
      // { actionType: 'ADD' | 'REMOVE' | 'REMOVE_ALL_ROLES', brandCode?, roleName?, row }
      pendingAction: null,
      actionInProgress: false,
      toast: null,
      toastTimer: null,
    }
  },
  computed: {
    ...mapGetters({
      allUserRoles: 'authorization/get',
      dealerSettings: 'user/dealerSettings',
    }),
    ...mapState('user', ['dealerId']),
    canModifyAdminRights() {
      return this.dealerSettings.isImporter
    },
    brands() {
      return [
        { code: 'V', textShort: 'V', textLong: 'Volkswagen' },
        { code: 'A', textShort: 'A', textLong: 'Audi' },
        { code: 'S', textShort: 'S', textLong: 'Seat' },
        { code: 'C', textShort: 'C', textLong: 'Skoda' },
        { code: 'N', textShort: 'BW', textLong: 'VW Bedrijfswagens' },
      ]
    },
    isGroupByRole() {
      return this.selectedViewMode === 'GROUP_BY_ROLE'
    },
    sortedUserRoles() {
      if (!this.allUserRoles?.AuthorizationLabels) return []

      return this.allUserRoles.AuthorizationLabels.filter(
        (role) => role.CanModify || role.RoleName == 'ADMIN'
      ).sort((role1, role2) => role2.CanModify - role1.CanModify)
    },
    rolesHeaders() {
      return this.sortedUserRoles.map((role) => ({
        text: this.capitalizeFirstLetter(role.Description),
        value: role.RoleName,
      }))
    },
    fixedColumns() {
      return [
        {
          key: 'UserPonId',
          label: this.i18n.t('/authorisationmodule/userponid'),
          width: '210px',
          filter: 'text',
          placeholder: 'Zoek e-mailadres...',
          headerClass: 'authorization-module-page__user-th',
        },
        {
          key: 'LastLoginDate',
          label: this.i18n.t('/authorisationmodule/lastlogindate'),
          width: '150px',
        },
        {
          key: 'ChangedDate',
          label: this.i18n.t('/authorisationmodule/changeddate'),
          width: '150px',
        },
        {
          key: 'ChangedBy',
          label: this.i18n.t('/authorisationmodule/changedby'),
          width: '210px',
          filter: 'text',
          placeholder: 'Zoek e-mailadres...',
        },
      ]
    },
    // One column per role (group-by-rol) or per brand (group-by-merk). Their
    // headers are custom layouts rendered through SortableTableHeader's
    // header-<key> slots; the cells hold multi-value chip rows, so they are
    // never sortable or filterable.
    matrixColumns() {
      if (this.isGroupByRole) {
        return this.rolesHeaders.map((role) => ({
          key: `role:${role.value}`,
          label: role.text,
          width: '180px',
          sortable: false,
          headerClass: 'authorization-module-page__matrix-th',
        }))
      }
      return this.brands.map((brand) => ({
        key: `brand:${brand.code}`,
        label: brand.textLong,
        brandCode: brand.code,
        width: `${this.rolesHeaders.length * ROLE_SLOT_WIDTH}px`,
        sortable: false,
        headerClass: 'authorization-module-page__matrix-th',
      }))
    },
    tableColumns() {
      return [...this.fixedColumns, ...this.matrixColumns]
    },
    // Sum of the declared column widths plus the actions column. Without a
    // definite table width, `table-layout: fixed` lets cell content inflate the
    // columns instead of the other way round.
    tableMinWidth() {
      const columns = this.tableColumns.reduce(
        (sum, col) => sum + (parseFloat(col.width) || 140),
        0
      )
      return `${columns + ACTIONS_COLUMN_WIDTH}px`
    },
    tableData() {
      if (!this.allUserRoles?.Authorizations) return []

      return this.allUserRoles.Authorizations.map((roleAuthorization) => {
        const lastLogin = roleAuthorization.Mutations.filter((login) => !!login.LastLoginDate)
          .sort((data1, data2) => (data1.LastLoginDate < data2.LastLoginDate ? 1 : -1))
          .find((x) => !!x)
        const lastChangedBy = roleAuthorization.Mutations.filter((login) => !!login.ChangedDate)
          .sort((data1, data2) => (data1.ChangedDate < data2.ChangedDate ? 1 : -1))
          .find((x) => !!x)

        return {
          DealerId: roleAuthorization.DealerId,
          UserPonId: roleAuthorization.UserPonId,
          LastLoginDate: this.formatDate(lastLogin?.LastLoginDate),
          LastLoginDateRaw: lastLogin?.LastLoginDate || null,
          ChangedDate: this.formatDate(lastChangedBy?.ChangedDate),
          ChangedDateRaw: lastChangedBy?.ChangedDate || null,
          ChangedBy: lastChangedBy?.ChangedBy,
          grants: new Set(
            roleAuthorization.AuthorizationRoles.map((role) => `${role.RoleName}:${role.Brand}`)
          ),
        }
      })
    },
    // The global search scans the identifying columns except ChangedBy,
    // case-insensitively; the per-column filters stack on top of it.
    searchedRows() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.tableData

      return this.tableData.filter((row) =>
        [row.UserPonId, row.LastLoginDate, row.ChangedDate].some(
          (value) => value != null && String(value).toLowerCase().includes(query)
        )
      )
    },
    filteredRows() {
      return this.searchedRows.filter((row) => {
        if (
          this.filters.UserPonId &&
          !(row.UserPonId || '').toLowerCase().includes(this.filters.UserPonId.toLowerCase())
        )
          return false
        if (
          this.filters.ChangedBy &&
          !(row.ChangedBy || '').toLowerCase().includes(this.filters.ChangedBy.toLowerCase())
        )
          return false
        return true
      })
    },
    sortedRows() {
      if (!this.sortColumn) return this.filteredRows
      const col = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return [...this.filteredRows].sort((a, b) => {
        if (col === 'LastLoginDate' || col === 'ChangedDate') {
          const rawKey = `${col}Raw`
          const valA = a[rawKey]
            ? moment.utc(a[rawKey]).valueOf()
            : dir === 1
              ? Infinity
              : -Infinity
          const valB = b[rawKey]
            ? moment.utc(b[rawKey]).valueOf()
            : dir === 1
              ? Infinity
              : -Infinity
          return (valA - valB) * dir
        }
        return String(a[col] ?? '').localeCompare(String(b[col] ?? ''), 'nl') * dir
      })
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedRows.slice(start, start + this.pageSize)
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.UserPonId) {
        chips.push({
          key: 'UserPonId',
          label: `${this.i18n.t('/authorisationmodule/userponid')}: "${this.filters.UserPonId}"`,
        })
      }
      if (this.filters.ChangedBy) {
        chips.push({
          key: 'ChangedBy',
          label: `${this.i18n.t('/authorisationmodule/changedby')}: "${this.filters.ChangedBy}"`,
        })
      }
      return chips
    },
    confirmActionText() {
      if (!this.pendingAction) return ''

      const params = {
        ponId: this.pendingAction.row.UserPonId,
        role: this.roleDescription(this.pendingAction.roleName),
        brand: this.brandName(this.pendingAction.brandCode),
      }

      switch (this.pendingAction.actionType) {
        case 'ADD':
          return this.i18n.t('/authorisationmodule/prompt/add', params)
        case 'REMOVE':
          return this.i18n.t('/authorisationmodule/prompt/remove', params)
        case 'REMOVE_ALL_ROLES':
          return this.i18n.t('/authorisationmodule/prompt/removeall', { ponId: params.ponId })
        default:
          return ''
      }
    },
    confirmButtonLabel() {
      return this.pendingAction?.actionType === 'ADD' ? 'Toevoegen' : 'Verwijderen'
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
    searchQuery() {
      this.currentPage = 1
    },
  },
  async mounted() {
    this.searchDealerId = this.dealerId
    await this.fetchAllUserRoles()
  },
  beforeUnmount() {
    clearTimeout(this.toastTimer)
  },
  methods: {
    getBrandBadgeClass,
    getBrandUrlByCode,
    async fetchAllUserRoles() {
      this.isLoading = true
      try {
        await authorizationService.getAllUserRoles(this.searchDealerId)
      } finally {
        this.isLoading = false
      }
    },
    matrixSlotName(col) {
      return `header-${col.key}`
    },
    brandColor(brandCode) {
      return `var(--ds-color-brand-${getBrandUrlByCode(brandCode)})`
    },
    brandName(brandCode) {
      return this.brands.find((brand) => brand.code === brandCode)?.textLong
    },
    roleDescription(roleName) {
      return this.rolesHeaders.find((role) => role.value === roleName)?.text
    },
    isAllowed(row, roleName, brandCode) {
      return row.grants.has(`${roleName}:${brandCode}`)
    },
    isChipDisabled(roleName) {
      return !this.canModifyAdminRights && roleName == 'ADMIN'
    },
    chipTitle(row, role, brand) {
      const prefix = this.isAllowed(row, role.value, brand.code) ? 'Verwijder' : 'Voeg toe:'
      return `${prefix} ${role.text} — ${brand.textLong}`
    },
    capitalizeFirstLetter(val) {
      return String(val).charAt(0).toUpperCase() + String(val).slice(1)
    },
    formatDate(date) {
      if (!date) return null

      const momentDate = moment.utc(date)
      if (!momentDate.isValid()) return null

      return momentDate.format('DD-MM-YYYY HH:mm')
    },
    requestToggle(row, brandCode, roleName) {
      const actionType = this.isAllowed(row, roleName, brandCode) ? 'REMOVE' : 'ADD'
      this.pendingAction = { actionType, brandCode, roleName, row }
    },
    showDeleteConfirmModal(row) {
      this.pendingAction = { actionType: 'REMOVE_ALL_ROLES', row }
    },
    async confirmAction() {
      const { actionType, brandCode, roleName, row } = this.pendingAction
      const params = {
        brand: brandCode,
        dealerId: row.DealerId,
        userPonId: row.UserPonId,
        roleName: roleName,
        action: actionType,
      }

      this.actionInProgress = true
      try {
        if (actionType === 'REMOVE_ALL_ROLES') {
          await authorizationService.deleteAdminAuthorization(params)
        } else {
          await authorizationService.postAdminAuthorization(params)
        }
        // The store is not updated by the mutation calls, so refresh it to make
        // the chips reflect the new truth.
        await authorizationService.getAllUserRoles(this.searchDealerId)
        this.showToast(
          actionType === 'ADD' ? 'success' : 'warning',
          this.successMessage(actionType, roleName, brandCode, row)
        )
        this.pendingAction = null
      } catch (error) {
        console.error('Failed to update authorization:', error)
        this.showToast('error', 'Het wijzigen van de rechten is mislukt. Probeer het opnieuw.')
      } finally {
        this.actionInProgress = false
      }
    },
    // Success and warning toasts dismiss themselves; an error stays until the
    // user closes it.
    showToast(type, message) {
      clearTimeout(this.toastTimer)
      this.toast = { type, message }
      if (type !== 'error') {
        this.toastTimer = setTimeout(() => {
          this.toast = null
        }, 5000)
      }
    },
    dismissToast() {
      clearTimeout(this.toastTimer)
      this.toast = null
    },
    successMessage(actionType, roleName, brandCode, row) {
      if (actionType === 'REMOVE_ALL_ROLES') {
        return `Alle rechten van gebruiker ${row.UserPonId} zijn verwijderd.`
      }
      const verb = actionType === 'ADD' ? 'toegevoegd aan' : 'verwijderd bij'
      return `${this.roleDescription(roleName)} voor ${this.brandName(brandCode)} ${verb} gebruiker ${row.UserPonId}.`
    },
    closeActionPopup() {
      this.pendingAction = null
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc'
        } else {
          this.sortColumn = null
          this.sortDirection = 'asc'
        }
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    setFilter(key, value) {
      this.filters[key] = value
    },
    clearMultipleFilters(keys) {
      keys.forEach((key) => {
        this.filters[key] = ''
      })
    },
    removeFilter(key) {
      this.filters[key] = ''
    },
    clearFilters() {
      this.filters = {
        UserPonId: '',
        ChangedBy: '',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout for this page only. The table, badges, buttons, pagination, dialog
// and toast all come from the design system (styles/ds/). See
// styles/ds/README.md before adding anything here.

// ---- Toolbar: search, view-mode switch, and (importer-only) dealer picker ----

.authorization-module-page__toolbar {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
  flex-wrap: wrap;
  padding: var(--ds-space-4) var(--ds-space-5);
  margin-bottom: var(--ds-space-4);
  border: 1px solid var(--ds-color-border);
  border-radius: var(--ds-radius-md);
  background: var(--ds-color-accent-subtle);
}

.authorization-module-page__search {
  position: relative;
  flex: 1 1 260px;
  min-width: 0;
  max-width: 380px;

  > svg {
    position: absolute;
    top: 50%;
    left: 14px;
    transform: translateY(-50%);
    color: var(--ds-color-text-subtle);
    pointer-events: none;
  }

  input {
    padding-left: 40px;
    border-radius: var(--ds-radius-full);
  }
}

.authorization-module-page__dealer {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-left: auto;

  .ds-combobox {
    width: 280px;
  }
}

.authorization-module-page__dealer-icon {
  flex: 0 0 auto;
  color: var(--ds-color-brand);
}

.authorization-module-page__dealer-label {
  font-size: var(--ds-font-size-sm);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ds-color-brand);
}

@media (max-width: 760px) {
  .authorization-module-page__dealer {
    flex: 1 1 100%;
    margin-left: 0;

    .ds-combobox {
      flex: 1;
      width: auto;
    }
  }
}

// ---- Fixed info cells ----

.authorization-module-page__user {
  font-weight: 700;
  word-break: break-all;
}

.authorization-module-page__nodate {
  color: var(--ds-color-text-subtle);
  font-style: italic;
}

// The user column stays put while the grant matrix scrolls sideways. The <th>
// class is applied inside SortableTableHeader, hence the :deep().
:deep(.authorization-module-page__user-th) {
  left: 0;
  z-index: 11;
}

.authorization-module-page__user-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--ds-color-surface);
}

// ---- Grant chips: one button per role x brand, colored by the brand ----

.authorization-module-page__chiprow {
  display: flex;
  flex-wrap: nowrap;
  gap: 5px;
  align-items: center;

  &--stretch {
    width: 100%;
  }
}

.authorization-module-page__chip {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 27px;
  height: 23px;
  padding: 0;
  border: 0;
  border-radius: var(--ds-radius-sm);
  color: var(--ds-color-surface);
  cursor: pointer;
  transition:
    filter 0.12s ease,
    transform 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    display: block;
  }

  // In group-by-merk mode the chips stretch to fill their role slot, so their
  // edges line up with the rotated role labels above.
  &--stretch {
    flex: 1 1 0;
    width: auto;
    height: 30px;
  }
}

.authorization-module-page__chip-off {
  opacity: 0.32;
}

// Zero the horizontal cell padding for grant cells in group-by-merk mode, so
// each chip's left edge starts at the same x-origin as the header's role slot.
.authorization-module-page__matrix-cell--stretch {
  padding: 11px 0;
}

// ---- Matrix column headers ----

:deep(.authorization-module-page__matrix-th) {
  vertical-align: bottom;
}

// Group-by-rol: role name above a row of brand-code badges.
.authorization-module-page__matrix-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  width: 100%;
  padding: 10px 14px;

  &--brand {
    gap: 0;
    padding: 0;
  }
}

.authorization-module-page__matrix-role-label {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.35;
  white-space: normal;
  color: var(--ds-color-text-subtle);
}

.authorization-module-page__matrix-brands {
  display: flex;
  gap: 5px;
  flex-wrap: nowrap;

  // Fixed-size badges, so they align with the 27px chips in the cells below.
  .ds-badge {
    width: 27px;
    min-width: 0;
    height: 23px;
    padding: 0;
    font-size: 10.5px;
  }
}

// Group-by-merk: rotated role labels above a band in the brand's color. Each
// slot is 55px wide; the label text is longer and deliberately leans into the
// neighbouring (transparent) headers instead of costing column width.
.authorization-module-page__matrix-roles {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 180px;
  padding: 0 0 5px;
  overflow: visible;

  > div {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: 5px;
    white-space: nowrap;
    text-align: left;
    transform: translateX(15px) rotate(-30deg);
    transform-origin: left bottom;
    overflow: visible;
    font-size: var(--ds-font-size-xs);
    font-weight: 700;
    color: var(--ds-color-text);

    > div {
      position: relative;
      z-index: 1;
      display: inline-block;
      width: max-content;
      border-left: 1px solid var(--ds-color-border);
      padding-left: 4px;
    }
  }
}

.authorization-module-page__matrix-brand {
  width: 100%;
  padding: 6px 4px;
  text-align: center;
  font-weight: 700;
  color: var(--ds-color-surface);

  // Cross-brand identity colors — an Audi column stays Audi red whatever skin
  // the app is running. See the cross-brand palette in styles/ds/_tokens.scss.
  &--volkswagen {
    background: var(--ds-color-brand-volkswagen);
  }

  &--vwbedrijfswagens {
    background: var(--ds-color-brand-vwbedrijfswagens);
  }

  &--audi {
    background: var(--ds-color-brand-audi);
  }

  &--seat {
    background: var(--ds-color-brand-seat);
  }

  &--skoda {
    background: var(--ds-color-brand-skoda);
  }
}

// In group-by-merk mode the rotated labels lean over the neighbouring headers,
// so every header cell goes transparent (an opaque one would paint over the
// leaning text) and the <thead> itself becomes the one sticky, opaque backdrop
// that blocks scrolled rows.
.authorization-module-page__table--rotated {
  :deep(thead) {
    position: sticky;
    top: 0;
    z-index: 8;
    background: var(--ds-color-surface-header);
  }

  :deep(.ds-table__header-cell) {
    position: static;
    background: transparent;
  }

  // Except the sticky user column, which needs its own opaque backdrop while
  // the matrix scrolls sideways underneath it.
  :deep(.authorization-module-page__user-th) {
    position: sticky;
    background: var(--ds-color-surface-header);
  }
}
</style>
